import { useEffect, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, Edit, Lock, CheckCircle } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";

export const PayrollRunTable = ({
  employees,
  selectedEmployees,
  setSelectedEmployees,
  payrollStatus,
  showDaysWorked = true,
  onLock,
  onComplete,
  onEditEmployee,
  isTermination = false,
  isOneTime = false,
}) => {
  const { can } = usePermission();
  const { canEdit } = createCanHelpers(can);

  /* --------------------------------------------
     DERIVED: selectable employees (NOT skipped)
  ---------------------------------------------*/
  const selectableEmployees = useMemo(
    () => employees.filter((e) => !e.isSkipped).map((e) => e.employee_id),
    [employees],
  );

  /* --------------------------------------------
     🔧 AUTO-CLEAN SELECTION (SAFE)
     Only update if something actually changed
  ---------------------------------------------*/
  useEffect(() => {
    const cleaned = selectedEmployees.filter((id) =>
      selectableEmployees.includes(id),
    );

    if (cleaned.length !== selectedEmployees.length) {
      setSelectedEmployees(cleaned);
    }
  }, [selectableEmployees, selectedEmployees, setSelectedEmployees]);

  /* --------------------------------------------
     SELECT-ALL LOGIC
  ---------------------------------------------*/
  const allSelected =
    selectableEmployees.length > 0 &&
    selectableEmployees.every((id) => selectedEmployees.includes(id));

  const handleSelectAll = () => {
    if (isTermination) return;

    setSelectedEmployees(allSelected ? [] : selectableEmployees);
  };

  const calculateTotalDeductions = (employee) => {
    // total_deductions is the authoritative full total from the backend/sheet:
    // custom deduction lines + statutory + loan EMI (everything except LOP, which
    // is already reflected in the prorated gross_pay). Net = gross_pay - this.
    // Do NOT re-add deductions_list or loan_deductions here — they are already
    // included, and adding them double-counted the loan (Gross - Deductions ≠ Net).
    return Number(employee.total_deductions || 0);
  };

  /* --------------------------------------------
     TOTALS (ONLY SELECTED & NOT SKIPPED)
  ---------------------------------------------*/
  const totals = employees
    .filter((e) => !e.isSkipped && selectedEmployees.includes(e.employee_id))
    .reduce(
      (acc, e) => {
        acc.gross += Number(e.gross_pay || 0);
        acc.deductions += calculateTotalDeductions(e);
        acc.net += Number(e.net_pay || 0);
        return acc;
      },
      { gross: 0, deductions: 0, net: 0 },
    );

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        {/* TABLE */}
        <div className="border dark:border-gray-700 rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {!isTermination && payrollStatus !== "locked" && (
                  <TableHead className="w-12">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                )}
                <TableHead>Employee</TableHead>
                <TableHead>Gross</TableHead>
                {showDaysWorked && !isOneTime && (
                  <TableHead title="Unpaid (LOP) days this period. Salary runs on a standard 30-day month, so 0 = full pay.">
                    Unpaid Days
                  </TableHead>
                )}
                <TableHead>Deductions</TableHead>
                <TableHead className="text-right">Net Pay</TableHead>
                {payrollStatus !== "locked" && <TableHead>Actions</TableHead>}
              </TableRow>
            </TableHeader>

            <TableBody>
              {employees.map((e) => {
                const isSkipped = e.isSkipped;

                return (
                  <TableRow
                    key={e.employee_id}
                    className={
                      isSkipped ? "bg-orange-50 dark:bg-orange-950/20" : ""
                    }
                  >
                    {!isTermination && payrollStatus !== "locked" && (
                      <TableCell>
                        <Checkbox
                          checked={selectedEmployees.includes(e.employee_id)}
                          disabled={isSkipped}
                          onCheckedChange={() =>
                            setSelectedEmployees((prev) =>
                              prev.includes(e.employee_id)
                                ? prev.filter((id) => id !== e.employee_id)
                                : [...prev, e.employee_id],
                            )
                          }
                        />
                      </TableCell>
                    )}

                    <TableCell>
                      <p className="font-medium">{e.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {e.empId} • {e.department}
                      </p>
                    </TableCell>

                    {isSkipped ? (
                      <TableCell colSpan={showDaysWorked ? 5 : 4}>
                        <div className="font-semibold text-orange-700 dark:text-orange-400">
                          🚫 Exit in progress — excluded from this payroll
                        </div>
                      </TableCell>
                    ) : (
                      <>
                        <TableCell>{formatCurrency(e.gross_pay)}</TableCell>

                        {showDaysWorked && !isOneTime && (
                          <TableCell>
                            {e.total_unpaid_leaves == null ? (
                              "-"
                            ) : e.total_unpaid_leaves > 0 ? (
                              <span className="font-medium text-amber-600 dark:text-amber-400">
                                {e.total_unpaid_leaves}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">0</span>
                            )}
                          </TableCell>
                        )}

                        <TableCell>
                          {formatCurrency(calculateTotalDeductions(e))}
                        </TableCell>

                        <TableCell className="text-right font-bold">
                          {formatCurrency(e.net_pay)}
                        </TableCell>

                        {payrollStatus !== "locked" && (
                          <TableCell>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => onEditEmployee(e)}
                              disabled={!canEdit("run_payroll")}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* FOOTER */}
        <div className="flex justify-between items-center pt-4 border-t dark:border-gray-700">
          <div className="text-sm">
            Net Pay: <strong>{formatCurrency(totals.net)}</strong>
          </div>

          {payrollStatus === "draft" && (
            <Button onClick={onLock} disabled={selectedEmployees.length === 0 || !canEdit("run_payroll")}>
              <Lock className="h-4 w-4 mr-2" />
              Lock Payroll
            </Button>
          )}

          {payrollStatus === "locked" && (
            <Button disabled={!canEdit("run_payroll")} onClick={onComplete}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Record Disbursement & Generate Payslips
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
